// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Бриф — marketing assistant chat UI. Faithful React port of the Claude Design
// prototype (Бриф.dc.html + Бриф Mobile.dc.html). Desktop = persistent sidebar;
// mobile (< 768px) = top bar + drawer + bottom composer.

import { useState, type CSSProperties, type ComponentType, type ReactNode } from 'react';
import { CalendarDays, LayoutGrid, LayoutTemplate, ListChecks, Plug, Radar, Settings } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  CHATS,
  DOT_COLORS,
  MODES,
  platformLabel,
  sourceModeLabel,
  strategyLabel,
  SUGGESTIONS,
  THINKING_LABELS,
  type BriefMessage,
  type CalendarItemType,
  type CopyVariant,
} from './data';
import { hashtags, translate } from './promo/chatApi';
import type { Platform } from './promo/types';
import { useAuthStore } from '@/stores/authStore';
import { useBrief, type BriefController, type BriefScreen } from './useBrief';
import { CalendarScreen, ChannelsScreen, OverviewScreen, QueueScreen } from './OpsScreens';
import { TemplatesScreen } from './screens/Templates';
import { SettingsSheet } from './screens/SettingsSheet';
import { RadarScreen } from './screens/Radar';
import { C, FONT_ROOT, mono, SANS } from './theme';

// ── Current operator (real authed user, not a placeholder persona) ──

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Суперадминистратор',
  content_manager: 'Контент-менеджер',
  analyst: 'Аналитик',
  support: 'Поддержка',
};

interface Profile {
  name: string;
  initial: string;
  roleLabel: string;
}

function useProfile(): Profile {
  const user = useAuthStore((s) => s.user);
  const name = user?.username?.trim() || 'Оператор';
  const initial = (name[0] || 'О').toUpperCase();
  const roleLabel = (user?.role && ROLE_LABELS[user.role]) || 'Оператор';
  return { name, initial, roleLabel };
}

// ── Shared bits ──────────────────────────────────────────────────

function Logo({ size = 30, radius = 9, font = 16 }: { size?: number; radius?: number; font?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: C.ink,
        color: C.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: font,
        letterSpacing: '-.02em',
        flexShrink: 0,
      }}
    >
      Б
    </div>
  );
}

function ThinkingDots({ label, size = 30 }: { label: string; size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: size, color: C.faint2 }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 0.2, 0.4].map((d, i) => (
          <span
            key={i}
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: C.faint,
              display: 'inline-block',
              animation: `briefPulse 1.2s infinite ${d}s`,
            }}
          />
        ))}
      </div>
      <span style={{ fontSize: 13 }}>{label}</span>
    </div>
  );
}

function ModeChips({ controller, mobile }: { controller: BriefController; mobile?: boolean }) {
  const { mode, setMode } = controller;
  return (
    <>
      {MODES.map((md) => {
        const active = md.id === mode;
        const style: CSSProperties = {
          fontFamily: SANS,
          fontSize: mobile ? 13 : 12.5,
          whiteSpace: 'nowrap',
          borderRadius: mobile ? 10 : 9,
          padding: mobile ? '8px 14px' : '7px 13px',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'all .12s',
          ...(active
            ? { background: C.ink, color: C.white, border: `1px solid ${C.ink}`, fontWeight: 600 }
            : { background: 'transparent', color: C.sub, border: `1px solid ${C.line2}` }),
        };
        return (
          <button key={md.id} onClick={() => setMode(md.id)} style={style}>
            {md.label}
          </button>
        );
      })}
    </>
  );
}

function SendButton({ controller, size = 34, font = 17 }: { controller: BriefController; size?: number; font?: number }) {
  const canSend = controller.draft.trim().length > 0;
  return (
    <button
      onClick={() => controller.send()}
      style={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        border: 'none',
        cursor: canSend ? 'pointer' : 'default',
        fontSize: font,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(canSend ? { background: C.ink, color: C.white } : { background: C.line3, color: C.faint }),
      }}
    >
      ↑
    </button>
  );
}

// ── Section navigation (hybrid IA) ───────────────────────────────

const NAV: { screen: Exclude<BriefScreen, 'chat'>; label: string; Icon: ComponentType<{ size?: number }> }[] = [
  { screen: 'radar', label: 'Response Radar', Icon: Radar },
  { screen: 'overview', label: 'Обзор', Icon: LayoutGrid },
  { screen: 'queue', label: 'Очередь', Icon: ListChecks },
  { screen: 'calendar', label: 'Календарь', Icon: CalendarDays },
  { screen: 'templates', label: 'Шаблоны', Icon: LayoutTemplate },
  { screen: 'channels', label: 'Каналы', Icon: Plug },
];

function SidebarNav({ controller, onNavigate }: { controller: BriefController; onNavigate?: () => void }) {
  return (
    <div style={{ padding: '10px 8px 2px' }}>
      <div style={mono({ padding: '0 14px 8px', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint })}>Разделы</div>
      {NAV.map(({ screen, label, Icon }) => {
        const active = controller.screen === screen;
        return (
          <div
            key={screen}
            onClick={() => {
              controller.setScreen(screen);
              onNavigate?.();
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, marginBottom: 1, color: active ? C.ink : C.ink2, background: active ? '#e8e4dd' : 'transparent' }}
          >
            <span style={{ display: 'flex', color: active ? C.ink : C.faint }}>
              <Icon size={16} />
            </span>
            <span style={{ fontWeight: active ? 600 : 400 }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Link to the legacy "classic" publisher console (full-feature reference UI). */
function ClassicConsoleLink() {
  return (
    <a
      href="/classic"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '9px 14px',
        margin: '4px 12px 2px',
        borderRadius: 9,
        textDecoration: 'none',
        fontFamily: SANS,
        fontSize: 12.5,
        color: C.muted,
        border: `1px solid ${C.line2}`,
        background: 'transparent',
      }}
    >
      <span style={{ fontSize: 13 }}>⌗</span>
      <span>Классическая консоль</span>
      <span style={{ marginLeft: 'auto', color: C.faint }}>→</span>
    </a>
  );
}

function OpsRouter({ controller, mobile }: { controller: BriefController; mobile?: boolean }) {
  switch (controller.screen) {
    case 'radar':
      return <RadarScreen mobile={mobile} />;
    case 'overview':
      return <OverviewScreen mobile={mobile} onOpenQueue={() => controller.setScreen('queue')} />;
    case 'queue':
      return <QueueScreen mobile={mobile} />;
    case 'calendar':
      return <CalendarScreen mobile={mobile} />;
    case 'templates':
      return <TemplatesScreen controller={controller} mobile={mobile} />;
    case 'channels':
      return <ChannelsScreen mobile={mobile} />;
    default:
      return null;
  }
}

// ── Message content renderers ────────────────────────────────────

/**
 * A single copy-variant card. Hashtags/translate (B7) are handled with local,
 * display-only state — these demo cards are not part of the live publish pipeline
 * (that path uses the DraftCard), so there's nothing to thread into a post here.
 */
function CopyVariantCard({ v, controller }: { v: CopyVariant; controller: BriefController }) {
  const [tags, setTags] = useState<string[] | null>(null);
  const [translation, setTranslation] = useState<{ language: 'ru' | 'en'; body: string } | null>(null);
  const [busy, setBusy] = useState<null | 'tags' | 'tr'>(null);
  const lang: 'ru' | 'en' = /[а-яё]/i.test(v.body) ? 'ru' : 'en';

  const addTags = () => {
    setBusy('tags');
    void hashtags({ body: `${v.headline}\n\n${v.body}`, platform: 'telegram' }).then(
      (res) => {
        const list = (res.hashtags ?? []).map((t) => t.replace(/^#/, '')).filter(Boolean);
        if (list.length === 0) controller.notify('Хэштеги не предложены');
        else setTags(list);
      },
      (e) => controller.notify(e instanceof Error ? e.message : 'Не удалось подобрать хэштеги'),
    ).finally(() => setBusy(null));
  };

  const doTranslate = () => {
    setBusy('tr');
    const direction = lang === 'ru' ? 'ru_to_en' : 'en_to_ru';
    void translate({ source: { title: v.headline, body: v.body, tags: [], language: lang }, direction, count: 1 }).then(
      (res) => {
        const variant = res.translations?.[0];
        const body = variant?.body?.trim();
        if (!body) controller.notify(res.error ?? 'Перевод недоступен');
        else setTranslation({ language: variant?.language ?? (lang === 'ru' ? 'en' : 'ru'), body });
      },
      (e) => controller.notify(e instanceof Error ? e.message : 'Не удалось перевести'),
    ).finally(() => setBusy(null));
  };

  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '16px 17px', background: C.white }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
        <span style={mono({ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, background: C.userBubble, borderRadius: 6, padding: '3px 8px' })}>
          {v.tone}
        </span>
        <span style={mono({ fontSize: 10, color: C.faint, marginLeft: 'auto' })}>{v.meta}</span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-.01em', marginBottom: 6 }}>{v.headline}</div>
      <div style={{ fontSize: 14, color: C.sub, lineHeight: 1.55 }}>{v.body}</div>

      {tags && tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 9 }}>
          {tags.map((t, ti) => (
            <span key={ti} style={mono({ fontSize: 11, color: C.muted })}>#{t.replace(/^#/, '')}</span>
          ))}
        </div>
      )}
      {translation && (
        <div style={{ marginTop: 10, borderLeft: '2px solid #ddd8d0', paddingLeft: 11 }}>
          <div style={mono({ fontSize: 9.5, letterSpacing: '.05em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 })}>
            {translation.language === 'en' ? 'English' : 'Русский'}
          </div>
          <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{translation.body}</div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 13, flexWrap: 'wrap' }}>
        <div style={{ background: C.ink, color: C.white, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600 }}>{v.cta}</div>
        <button
          onClick={addTags}
          disabled={busy != null}
          style={{ background: 'none', border: `1px solid ${C.line3}`, borderRadius: 8, padding: '7px 12px', fontFamily: SANS, fontSize: 12.5, color: C.sub, cursor: busy ? 'default' : 'pointer' }}
        >
          {busy === 'tags' ? '…' : '# Хэштеги'}
        </button>
        <button
          onClick={doTranslate}
          disabled={busy != null}
          style={{ background: 'none', border: `1px solid ${C.line3}`, borderRadius: 8, padding: '7px 12px', fontFamily: SANS, fontSize: 12.5, color: C.sub, cursor: busy ? 'default' : 'pointer' }}
        >
          {busy === 'tr' ? '…' : `Перевести (${lang === 'ru' ? 'EN' : 'RU'})`}
        </button>
        <button
          onClick={() => controller.copyText(`${v.headline}\n\n${v.body}`)}
          style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${C.line3}`, borderRadius: 8, padding: '7px 12px', fontFamily: SANS, fontSize: 12.5, color: C.sub, cursor: 'pointer' }}
        >
          Копировать
        </button>
      </div>
    </div>
  );
}

function CopyVariants({ m, controller }: { m: BriefMessage; controller: BriefController }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {m.variants!.map((v, i) => (
        <CopyVariantCard key={i} v={v} controller={controller} />
      ))}
    </div>
  );
}

function ImageGrid({ m, controller }: { m: BriefMessage; controller: BriefController }) {
  const c1 = m.creative ? '#efe9e1' : '#eceae6';
  const c2 = m.creative ? '#e3dbd0' : '#e0ddd6';
  const boxStyle: CSSProperties = {
    aspectRatio: '1 / 1',
    borderRadius: 12,
    border: `1px solid ${C.line3}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `repeating-linear-gradient(45deg, ${c1}, ${c1} 9px, ${c2} 9px, ${c2} 18px)`,
  };
  const promptLine = m.prompt ? `запрос: ${m.prompt.length > 42 ? `${m.prompt.slice(0, 42)}…` : m.prompt}` : '';
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {m.images!.map((img, i) => (
          <div key={i} style={boxStyle}>
            <div style={mono({ fontSize: 11, color: '#9a958c', background: 'rgba(255,255,255,.78)', borderRadius: 7, padding: '5px 9px' })}>{img.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 13, flexWrap: 'wrap' }}>
        <button onClick={() => controller.regen(m.id, m.mode!)} style={{ background: 'none', border: `1px solid ${C.line3}`, borderRadius: 9, padding: '8px 14px', fontFamily: SANS, fontSize: 13, color: C.ink2, cursor: 'pointer' }}>
          ↻ Сгенерировать ещё
        </button>
        <button onClick={() => controller.notify('Файлы готовятся к загрузке')} style={{ background: 'none', border: `1px solid ${C.line3}`, borderRadius: 9, padding: '8px 14px', fontFamily: SANS, fontSize: 13, color: C.ink2, cursor: 'pointer' }}>
          Скачать всё
        </button>
        {promptLine && <span style={mono({ fontSize: 11, color: C.faint })}>{promptLine}</span>}
      </div>
    </div>
  );
}

function IdeaList({ m }: { m: BriefMessage }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {m.ideas!.map((i, idx) => (
        <div key={idx} style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '16px 17px', background: C.white }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.01em' }}>{i.name}</div>
            <div style={mono({ fontSize: 10, color: C.faint, marginLeft: 'auto' })}>{i.kpi}</div>
          </div>
          <div style={{ fontSize: 14, color: C.sub, lineHeight: 1.55, marginBottom: 11 }}>{i.big}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {i.channels.map((ch, ci) => (
              <span key={ci} style={{ fontSize: 12, color: C.sub, background: '#f3f0eb', borderRadius: 7, padding: '4px 10px' }}>{ch}</span>
            ))}
          </div>
          <div style={{ fontSize: 13, color: C.ink2, borderLeft: '2px solid #ddd8d0', paddingLeft: 11 }}>
            <span style={{ color: C.faint }}>Крючок · </span>{i.hook}
          </div>
        </div>
      ))}
    </div>
  );
}

function CalendarTable({ m }: { m: BriefMessage }) {
  const days = m.calendar!;
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden', background: C.white }}>
      {days.map((d, di) => (
        <div key={di} style={{ display: 'flex', gap: 14, padding: '13px 16px', ...(di < days.length - 1 ? { borderBottom: `1px solid ${C.line4}` } : {}) }}>
          <div style={{ width: 54, flexShrink: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{d.day}</div>
            <div style={mono({ fontSize: 10, color: C.faint })}>{d.date}</div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {d.items.map((it, ii) => (
              <div key={ii} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: DOT_COLORS[it.type as CalendarItemType] || '#8a8a85' }} />
                <span style={mono({ fontSize: 10, color: C.muted, width: 78, flexShrink: 0 })}>{it.platform}</span>
                <span style={{ fontSize: 13.5, color: C.ink2 }}>{it.title}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Live Hermes pipeline renderers (same warm Бриф card style) ───

function MetaPill({ children }: { children: ReactNode }) {
  return (
    <span style={mono({ fontSize: 10.5, letterSpacing: '.02em', color: C.muted, background: C.chipBg, borderRadius: 7, padding: '4px 9px' })}>
      {children}
    </span>
  );
}

function PlanCardView({ m }: { m: BriefMessage }) {
  const p = m.plan!;
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '15px 17px', background: C.white }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}>
        <span style={mono({ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted })}>План кампании</span>
        {p.fallback && (
          <span style={mono({ fontSize: 9.5, letterSpacing: '.04em', textTransform: 'uppercase', color: '#b08968', border: '1px solid #e3d3c4', borderRadius: 6, padding: '2px 6px', marginLeft: 'auto' })}>
            rules
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 11 }}>
        <MetaPill>{p.language === 'ru' ? 'Русский' : 'English'}</MetaPill>
        <MetaPill>{strategyLabel(p.strategy)}</MetaPill>
        <MetaPill>{sourceModeLabel(p.sourceMode)}</MetaPill>
        {p.requireCover && <MetaPill>с обложкой</MetaPill>}
      </div>
      {p.topic && <div style={{ fontSize: 14, color: C.ink2, lineHeight: 1.5, marginBottom: 11 }}>{p.topic}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {p.platforms.map((pl) => (
          <span key={pl} style={{ fontSize: 12, color: C.sub, background: '#f3f0eb', borderRadius: 7, padding: '4px 10px' }}>{platformLabel(pl)}</span>
        ))}
      </div>
      {p.warning && <div style={{ fontSize: 12, color: '#b08968', marginTop: 10 }}>{p.warning}</div>}
    </div>
  );
}

function SuggestionsPicker({ m, controller }: { m: BriefMessage; controller: BriefController }) {
  const card = m.picker!;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {card.fallback && card.note && (
        <div style={mono({ fontSize: 11, color: C.faint })}>{card.note}</div>
      )}
      {card.suggestions.map((s) => {
        const active = card.pickedId === s.id;
        const locked = card.pickedId != null && !active;
        return (
          <div
            key={s.id}
            style={{
              border: `1px solid ${active ? C.ink : C.line}`,
              borderRadius: 14,
              padding: '16px 17px',
              background: C.white,
              opacity: locked ? 0.55 : 1,
              transition: 'all .14s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 7 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-.01em', flex: 1 }}>{s.angle}</div>
              {s.source.image_url && <MetaPill>обложка</MetaPill>}
            </div>
            <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.55, marginBottom: 11 }}>{s.rationale}</div>
            {s.source.title && (
              <div style={{ fontSize: 12.5, color: C.muted, borderLeft: '2px solid #ddd8d0', paddingLeft: 11, marginBottom: 12 }}>
                <span style={{ color: C.faint }}>Источник · </span>{s.source.title}
              </div>
            )}
            <button
              disabled={card.pickedId != null}
              onClick={() => controller.pickSuggestion(m.id, s.id)}
              style={{
                width: '100%',
                background: active ? C.ink : 'transparent',
                color: active ? C.white : C.ink2,
                border: `1px solid ${active ? C.ink : C.line3}`,
                borderRadius: 10,
                padding: '10px 14px',
                fontFamily: SANS,
                fontSize: 13.5,
                fontWeight: 600,
                cursor: card.pickedId != null ? 'default' : 'pointer',
              }}
            >
              {active ? '✓ Выбрано' : 'Выбрать этот вариант'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Small text-button used by the per-platform draft toolbar (B5 + B7). */
function DraftToolButton({ children, onClick, active }: { children: ReactNode; onClick: () => void; active?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? C.ink : 'none',
        color: active ? C.white : C.sub,
        border: `1px solid ${active ? C.ink : C.line3}`,
        borderRadius: 8,
        padding: '5px 11px',
        fontFamily: SANS,
        fontSize: 12.5,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function DraftPreview({ m, controller }: { m: BriefMessage; controller: BriefController }) {
  const card = m.draft!;
  const edits = card.edits ?? {};
  const extraTags = card.extraTags ?? {};
  const translated = card.translated ?? {};
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {card.platforms.map((d, i) => {
        const platform = d.platform as Platform;
        const editing = card.editing === platform;
        const editedBody = edits[platform];
        const shownBody = typeof editedBody === 'string' ? editedBody : d.body;
        const tags = [...(d.tags ?? []), ...(extraTags[platform] ?? [])];
        const tr = translated[platform];
        // Detect language for translate direction from the (possibly edited) body.
        const lang: 'ru' | 'en' = /[а-яё]/i.test(shownBody) ? 'ru' : 'en';
        return (
          <div key={i} style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 16px', background: C.white }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={mono({ fontSize: 10, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, background: C.userBubble, borderRadius: 6, padding: '3px 8px' })}>
                {platformLabel(platform)}
              </span>
              {typeof editedBody === 'string' && editedBody !== d.body && (
                <span style={mono({ fontSize: 9.5, letterSpacing: '.04em', textTransform: 'uppercase', color: '#b08968' })}>изменено</span>
              )}
            </div>
            {d.title && <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', marginBottom: 5 }}>{d.title}</div>}

            {editing ? (
              <textarea
                autoFocus
                value={shownBody}
                onChange={(e) => controller.setDraftEdit(m.id, platform, e.target.value)}
                onBlur={() => controller.setDraftEditing(m.id, null)}
                rows={4}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  border: `1px solid ${C.line2}`,
                  borderRadius: 11,
                  background: C.white,
                  fontFamily: SANS,
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: C.ink,
                  padding: '10px 12px',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: 90,
                }}
              />
            ) : (
              <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{shownBody}</div>
            )}

            {tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 9 }}>
                {tags.map((t, ti) => (
                  <span key={ti} style={mono({ fontSize: 11, color: C.muted })}>#{t.replace(/^#/, '')}</span>
                ))}
              </div>
            )}

            {tr && (
              <div style={{ marginTop: 10, borderLeft: '2px solid #ddd8d0', paddingLeft: 11 }}>
                <div style={mono({ fontSize: 9.5, letterSpacing: '.05em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 })}>
                  {tr.language === 'en' ? 'English' : 'Русский'}
                </div>
                <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{tr.body}</div>
                <button
                  type="button"
                  onClick={() => controller.clearTranslation(m.id, platform)}
                  style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, fontFamily: SANS, fontSize: 12, color: C.faint, cursor: 'pointer' }}
                >
                  Скрыть перевод
                </button>
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
              <DraftToolButton onClick={() => controller.setDraftEditing(m.id, editing ? null : platform)} active={editing}>
                {editing ? 'Готово' : 'Изменить'}
              </DraftToolButton>
              <DraftToolButton onClick={() => controller.appendHashtags(m.id, platform, shownBody)}># Хэштеги</DraftToolButton>
              <DraftToolButton onClick={() => controller.translateDraft(m.id, platform, shownBody, lang)}>
                Перевести ({lang === 'ru' ? 'EN' : 'RU'})
              </DraftToolButton>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChannelSelect({ m, controller }: { m: BriefMessage; controller: BriefController }) {
  const card = m.channels!;
  if (card.loading) return <ThinkingDots label="Загружаю каналы…" />;
  if (card.error) {
    return (
      <div style={{ border: '1px solid #e8d6cf', borderRadius: 14, padding: '14px 16px', background: '#fbf3f0', fontSize: 13.5, color: '#9a4a37' }}>
        {card.error}
      </div>
    );
  }
  const chosen = card.selected.length;
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 16px', background: C.white }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {card.options.map((o) => {
          const checked = card.selected.includes(o.id);
          const disabled = card.confirmed || !o.ready;
          return (
            <button
              key={o.id}
              disabled={disabled}
              onClick={() => controller.toggleChannel(m.id, o.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                width: '100%',
                textAlign: 'left',
                border: `1px solid ${checked ? C.ink : C.line3}`,
                background: checked ? '#f5f2ee' : 'transparent',
                borderRadius: 11,
                padding: '10px 12px',
                cursor: disabled ? 'default' : 'pointer',
                opacity: o.ready ? 1 : 0.5,
                fontFamily: SANS,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 6,
                  flexShrink: 0,
                  border: `1.5px solid ${checked ? C.ink : C.faint}`,
                  background: checked ? C.ink : 'transparent',
                  color: C.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                }}
              >
                {checked ? '✓' : ''}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: C.faint2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.detail}</span>
              </span>
              <span style={mono({ fontSize: 9.5, letterSpacing: '.04em', textTransform: 'uppercase', color: o.method === 'buffer' ? '#b08968' : C.faint, flexShrink: 0 })}>{o.method}</span>
            </button>
          );
        })}
      </div>
      {!card.confirmed ? (
        <button
          disabled={chosen === 0}
          onClick={() => controller.confirmChannels(m.id)}
          style={{
            width: '100%',
            marginTop: 12,
            background: chosen > 0 ? C.ink : C.line3,
            color: chosen > 0 ? C.white : C.faint,
            border: 'none',
            borderRadius: 11,
            padding: '11px 14px',
            fontFamily: SANS,
            fontSize: 14,
            fontWeight: 600,
            cursor: chosen > 0 ? 'pointer' : 'default',
          }}
        >
          Опубликовать в {chosen} {chosen === 1 ? 'канал' : 'каналах'}
        </button>
      ) : (
        <div style={mono({ fontSize: 11.5, color: C.faint, marginTop: 12, textAlign: 'center' })}>Каналы зафиксированы</div>
      )}
    </div>
  );
}

function PublishResultView({ m }: { m: BriefMessage }) {
  const card = m.publish!;
  const pending = card.status && card.status !== 'done' && card.status !== 'blocked';
  if (pending) {
    const label =
      card.status === 'creating' ? 'Создаю пост…' : card.status === 'preflight' ? 'Проверяю каналы…' : 'Публикую…';
    return <ThinkingDots label={label} />;
  }
  if (card.status === 'blocked') {
    return (
      <div style={{ border: '1px solid #e8d6cf', borderRadius: 14, padding: '14px 16px', background: '#fbf3f0', fontSize: 13.5, color: '#9a4a37' }}>
        Публикация заблокирована — проверьте подключение каналов в разделе «Каналы».
      </div>
    );
  }
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 16px', background: C.white, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {card.results.length === 0 && (
        <div style={{ fontSize: 13.5, color: C.sub }}>{card.message ?? 'Нет каналов для публикации.'}</div>
      )}
      {card.results.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: r.success ? '#5f8a5f' : '#c1442e' }} />
          <span style={{ fontSize: 13.5, color: C.ink2, fontWeight: 600 }}>{platformLabel(r.platform)}</span>
          {r.success ? (
            r.external_url ? (
              <a href={r.external_url} target="_blank" rel="noreferrer" style={mono({ fontSize: 11.5, color: C.muted, marginLeft: 'auto', textDecoration: 'underline' })}>
                открыть →
              </a>
            ) : (
              <span style={mono({ fontSize: 11.5, color: C.faint, marginLeft: 'auto' })}>опубликовано</span>
            )
          ) : (
            <span style={{ fontSize: 11.5, color: '#c1442e', marginLeft: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>{r.error ?? 'ошибка'}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function ErrorBubble({ m }: { m: BriefMessage }) {
  return (
    <div style={{ border: '1px solid #e8d6cf', borderRadius: 14, padding: '14px 16px', background: '#fbf3f0', fontSize: 13.5, color: '#9a4a37' }}>
      {m.text || 'Что-то пошло не так. Попробуйте ещё раз.'}
    </div>
  );
}

function MessageItem({ m, controller, mobile }: { m: BriefMessage; controller: BriefController; mobile?: boolean }) {
  if (m.role === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', animation: 'briefUp .3s ease both' }}>
        <div style={{ background: C.userBubble, border: '1px solid #ebe7e0', borderRadius: '18px 18px 6px 18px', padding: mobile ? '11px 15px' : '12px 16px', maxWidth: mobile ? '86%' : '78%', fontSize: mobile ? 14.5 : 15, color: C.ink2 }}>
          {m.text}
        </div>
      </div>
    );
  }

  const avatar = mobile ? 28 : 30;
  // Intro line shows for content messages that carry one. The `error` kind renders
  // its own bubble (text lives inside it), so suppress the duplicate intro there.
  const showIntro = m.kind !== 'thinking' && m.kind !== 'error' && !!m.text;

  return (
    <div style={{ display: 'flex', gap: mobile ? 10 : 13, animation: 'briefUp .3s ease both' }}>
      <Logo size={avatar} radius={mobile ? 8 : 9} font={14} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {m.kind === 'thinking' && <ThinkingDots label={THINKING_LABELS[m.mode!] || 'Думаю…'} size={avatar} />}
        {m.kind !== 'thinking' && showIntro && (
          <div style={{ fontSize: mobile ? 14.5 : 15, color: C.ink2, marginBottom: mobile ? 13 : 14 }}>{m.text}</div>
        )}
        {m.kind === 'copy' && <CopyVariants m={m} controller={controller} />}
        {m.kind === 'image' && <ImageGrid m={m} controller={controller} />}
        {m.kind === 'idea' && <IdeaList m={m} />}
        {m.kind === 'calendar' && <CalendarTable m={m} />}
        {m.kind === 'plan' && <PlanCardView m={m} />}
        {m.kind === 'suggestions' && <SuggestionsPicker m={m} controller={controller} />}
        {m.kind === 'draft' && <DraftPreview m={m} controller={controller} />}
        {m.kind === 'channels' && <ChannelSelect m={m} controller={controller} />}
        {m.kind === 'publish' && <PublishResultView m={m} />}
        {m.kind === 'error' && <ErrorBubble m={m} />}
      </div>
    </div>
  );
}

function Toast({ text, bottom }: { text: string; bottom: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom,
        left: '50%',
        transform: 'translateX(-50%)',
        background: C.ink,
        color: C.white,
        fontSize: 13,
        padding: '9px 16px',
        borderRadius: 10,
        boxShadow: '0 6px 20px rgba(26,25,22,.18)',
        animation: 'briefToast .25s ease both',
        zIndex: 60,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </div>
  );
}

// ── Desktop ──────────────────────────────────────────────────────

function DesktopSidebar({ controller, onLogout, onOpenSettings }: { controller: BriefController; onLogout?: () => void; onOpenSettings: () => void }) {
  const { activeChat, loadChat, goHome } = controller;
  const { name, initial, roleLabel } = useProfile();
  return (
    <aside style={{ width: 264, flexShrink: 0, background: C.sidebar, borderRight: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div onClick={goHome} style={{ padding: '18px 16px 12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
        <Logo />
        <div style={{ fontWeight: 700, fontSize: 18, letterSpacing: '-.01em' }}>Бриф</div>
        <div style={mono({ marginLeft: 'auto', fontSize: 10, color: C.faint2, border: `1px solid ${C.line2}`, borderRadius: 6, padding: '2px 6px' })}>beta</div>
      </div>

      <div style={{ padding: '6px 12px 4px' }}>
        <button onClick={goHome} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', background: C.ink, color: C.white, border: 'none', borderRadius: 12, fontFamily: SANS, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <span style={{ fontSize: 17, lineHeight: 1, marginTop: -1 }}>+</span>
          <span>Новый чат</span>
        </button>
      </div>

      <div style={{ padding: '8px 12px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', background: '#efece7', border: `1px solid ${C.line3}`, borderRadius: 10, color: C.faint2, fontSize: 13.5 }}>
          <span style={{ fontSize: 14 }}>⌕</span>
          <span>Поиск по чатам</span>
        </div>
      </div>

      <SidebarNav controller={controller} />

      {CHATS.length > 0 ? (
        <>
          <div style={mono({ padding: '14px 22px 6px', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint })}>Недавние</div>
          <nav className="brief-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
            {CHATS.map((c, i) => (
              <div
                key={i}
                onClick={() => loadChat(c, i)}
                style={{ padding: '9px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, color: C.ink2, marginBottom: 1, transition: 'background .12s', ...(activeChat === i ? { background: '#e8e4dd' } : {}) }}
              >
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                <div style={mono({ fontSize: 10, color: C.faint, marginTop: 2 })}>{c.tag}</div>
              </div>
            ))}
          </nav>
        </>
      ) : (
        <div style={{ flex: 1 }} />
      )}

      <ClassicConsoleLink />

      <div style={{ borderTop: `1px solid ${C.line}`, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div onClick={onLogout} title={onLogout ? 'Выйти' : undefined} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#d9d4cb', color: C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14, flexShrink: 0 }}>{initial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            <div style={{ fontSize: 11.5, color: C.faint2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleLabel}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenSettings}
          aria-label="Настройки"
          title="Настройки"
          style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 9, border: `1px solid ${C.line2}`, background: 'none', color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <Settings size={16} />
        </button>
      </div>
    </aside>
  );
}

function DesktopComposer({ controller, chat }: { controller: BriefController; chat?: boolean }) {
  const inner = (
    <div style={{ border: `1px solid ${C.line2}`, borderRadius: 24, background: C.white, boxShadow: '0 2px 18px rgba(26,25,22,.05)', padding: chat ? '13px 15px 11px' : '14px 16px 12px' }}>
      <textarea
        rows={1}
        value={controller.draft}
        onChange={(e) => controller.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            controller.send();
          }
        }}
        placeholder={chat ? 'Продолжить или уточнить запрос…' : 'Опишите задачу или выберите режим…'}
        style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', background: 'transparent', fontFamily: SANS, fontSize: 15.5, lineHeight: 1.5, color: C.ink, maxHeight: 160, minHeight: 24 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: chat ? 9 : 10 }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, border: `1px solid ${C.line2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, cursor: 'pointer', flexShrink: 0, fontSize: 16 }}>⊕</div>
        <div className="brief-scroll-hidden" style={{ display: 'flex', gap: 6, overflowX: 'auto', flex: 1 }}>
          <ModeChips controller={controller} />
        </div>
        <SendButton controller={controller} />
      </div>
    </div>
  );

  if (chat) {
    return (
      <div style={{ flexShrink: 0, padding: '0 24px 20px', background: 'linear-gradient(to top, #fff 70%, rgba(255,255,255,0))' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>{inner}</div>
      </div>
    );
  }
  return inner;
}

function DesktopHome({ controller }: { controller: BriefController }) {
  const { name } = useProfile();
  return (
    <div className="brief-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
      <div style={{ width: '100%', maxWidth: 720, animation: 'briefUp .4s ease both' }}>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <div style={mono({ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: C.faint, marginBottom: 14 })}>Маркетинг-ассистент</div>
          <h1 style={{ margin: 0, fontSize: 34, fontWeight: 700, letterSpacing: '-.02em' }}>Чем займёмся, {name}?</h1>
          <p style={{ margin: '10px 0 0', color: C.muted, fontSize: 16 }}>Опишите задачу или выберите режим — соберу тексты, креативы, идеи и контент-план.</p>
        </div>

        <DesktopComposer controller={controller} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(218px, 1fr))', gap: 10, marginTop: 18 }}>
          {SUGGESTIONS.map((s, i) => (
            <div
              key={i}
              onClick={() => controller.sendSuggestion(s)}
              style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 15px', cursor: 'pointer', background: C.white, transition: 'all .15s' }}
            >
              <div style={mono({ fontSize: 10, letterSpacing: '.07em', textTransform: 'uppercase', color: C.faint, marginBottom: 7 })}>{s.tag}</div>
              <div style={{ fontSize: 14, color: C.ink2, lineHeight: 1.4 }}>{s.title}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DesktopChat({ controller }: { controller: BriefController }) {
  const { activeChat, messages, scrollRef } = controller;
  const activeMeta = activeChat != null ? CHATS[activeChat] : null;
  const chatTitle = activeMeta ? activeMeta.title : 'Новый чат';
  const chatTag = activeMeta ? activeMeta.tag : MODES.find((m) => m.id === controller.mode)?.label || '';
  return (
    <>
      <div style={{ height: 56, flexShrink: 0, borderBottom: `1px solid ${C.line4}`, display: 'flex', alignItems: 'center', gap: 10, padding: '0 22px' }}>
        <div style={{ fontWeight: 600, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chatTitle}</div>
        <div style={mono({ fontSize: 10, color: C.faint, border: `1px solid ${C.line}`, borderRadius: 6, padding: '2px 7px' })}>{chatTag}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <div style={{ padding: '7px 13px', border: `1px solid ${C.line3}`, borderRadius: 9, fontSize: 13, color: C.sub, cursor: 'pointer' }}>Поделиться</div>
        </div>
      </div>

      <div ref={scrollRef} className="brief-scroll" style={{ flex: 1, overflowY: 'auto', padding: '28px 0' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 26 }}>
          {messages.map((m) => (
            <MessageItem key={m.id} m={m} controller={controller} />
          ))}
        </div>
      </div>

      <DesktopComposer controller={controller} chat />
    </>
  );
}

function DesktopLayout({ controller, onLogout }: { controller: BriefController; onLogout?: () => void }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', background: C.white, ...FONT_ROOT }}>
      <DesktopSidebar controller={controller} onLogout={onLogout} onOpenSettings={() => setSettingsOpen(true)} />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', minWidth: 0, position: 'relative' }}>
        {controller.screen !== 'chat' ? (
          <OpsRouter controller={controller} />
        ) : controller.view === 'home' ? (
          <DesktopHome controller={controller} />
        ) : (
          <DesktopChat controller={controller} />
        )}
        {controller.toast && <Toast text={controller.toast} bottom={104} />}
      </main>
      {settingsOpen && (
        <SettingsSheet timezone={controller.timezone} onSave={controller.setTimezone} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

// ── Mobile ───────────────────────────────────────────────────────

function MobileDrawer({ controller, onClose, onLogout, onOpenSettings }: { controller: BriefController; onClose: () => void; onLogout?: () => void; onOpenSettings: () => void }) {
  const { activeChat, loadChat, goHome } = controller;
  const { name, initial, roleLabel } = useProfile();
  return (
    <>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(26,25,22,.32)', zIndex: 40, animation: 'briefUp .2s ease both' }} />
      <aside style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 300, maxWidth: '84%', background: C.sidebar, borderRight: `1px solid ${C.line}`, zIndex: 50, display: 'flex', flexDirection: 'column', boxShadow: '6px 0 24px rgba(26,25,22,.12)' }}>
        <div style={{ padding: '18px 16px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo />
          <div style={{ fontWeight: 700, fontSize: 18 }}>Бриф</div>
          <div style={mono({ marginLeft: 'auto', fontSize: 10, color: C.faint2, border: `1px solid ${C.line2}`, borderRadius: 6, padding: '2px 6px' })}>beta</div>
        </div>
        <div style={{ padding: '6px 12px 4px' }}>
          <button onClick={() => { goHome(); onClose(); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '13px 12px', background: C.ink, color: C.white, border: 'none', borderRadius: 12, fontFamily: SANS, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <span style={{ fontSize: 17, lineHeight: 1, marginTop: -1 }}>+</span>
            <span>Новый чат</span>
          </button>
        </div>
        <div style={{ padding: '8px 12px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '11px 12px', background: '#efece7', border: `1px solid ${C.line3}`, borderRadius: 10, color: C.faint2, fontSize: 13.5 }}>
            <span style={{ fontSize: 14 }}>⌕</span>
            <span>Поиск по чатам</span>
          </div>
        </div>
        <SidebarNav controller={controller} onNavigate={onClose} />
        {CHATS.length > 0 ? (
          <>
            <div style={mono({ padding: '14px 22px 6px', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint })}>Недавние</div>
            <nav className="brief-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
              {CHATS.map((c, i) => (
                <div
                  key={i}
                  onClick={() => { loadChat(c, i); onClose(); }}
                  style={{ padding: '11px 12px', borderRadius: 9, cursor: 'pointer', fontSize: 13.5, color: C.ink2, marginBottom: 1, ...(activeChat === i ? { background: '#e8e4dd' } : {}) }}
                >
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                  <div style={mono({ fontSize: 10, color: C.faint, marginTop: 2 })}>{c.tag}</div>
                </div>
              ))}
            </nav>
          </>
        ) : (
          <div style={{ flex: 1 }} />
        )}
        <ClassicConsoleLink />

        <div style={{ borderTop: `1px solid ${C.line}`, padding: '14px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div onClick={onLogout} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#d9d4cb', color: C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14, flexShrink: 0 }}>{initial}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
              <div style={{ fontSize: 11.5, color: C.faint2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleLabel}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { onOpenSettings(); onClose(); }}
            aria-label="Настройки"
            style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 10, border: `1px solid ${C.line2}`, background: 'none', color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <Settings size={17} />
          </button>
        </div>
      </aside>
    </>
  );
}

function MobileHome({ controller }: { controller: BriefController }) {
  const { name } = useProfile();
  return (
    <div className="brief-scroll" style={{ flex: 1, overflowY: 'auto', padding: '24px 18px 16px', animation: 'briefUp .35s ease both' }}>
      <div style={mono({ fontSize: 10.5, letterSpacing: '.1em', textTransform: 'uppercase', color: C.faint, marginBottom: 10 })}>Маркетинг-ассистент</div>
      <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.2 }}>Чем займёмся,<br />{name}?</h1>
      <p style={{ margin: '9px 0 22px', color: C.muted, fontSize: 14.5 }}>Опишите задачу или выберите режим — соберу тексты, креативы, идеи и контент-план.</p>
      <div style={mono({ fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint, marginBottom: 10 })}>С чего начнём</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {SUGGESTIONS.map((s, i) => (
          <div key={i} onClick={() => controller.sendSuggestion(s)} style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 15px', cursor: 'pointer', background: C.white, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={mono({ fontSize: 9.5, letterSpacing: '.07em', textTransform: 'uppercase', color: C.faint, marginBottom: 5 })}>{s.tag}</div>
              <div style={{ fontSize: 14.5, color: C.ink2, lineHeight: 1.35 }}>{s.title}</div>
            </div>
            <span style={{ color: '#cfc9c0', fontSize: 18, flexShrink: 0 }}>›</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileChat({ controller }: { controller: BriefController }) {
  const { messages, scrollRef } = controller;
  return (
    <div ref={scrollRef} className="brief-scroll-hidden" style={{ flex: 1, overflowY: 'auto', padding: '20px 0' }}>
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        {messages.map((m) => (
          <MessageItem key={m.id} m={m} controller={controller} mobile />
        ))}
      </div>
    </div>
  );
}

function MobileComposer({ controller }: { controller: BriefController }) {
  const placeholder = controller.view === 'home' ? 'Опишите задачу…' : 'Продолжить запрос…';
  return (
    <div style={{ flexShrink: 0, borderTop: `1px solid ${C.line4}`, background: C.white, padding: '10px 12px calc(12px + env(safe-area-inset-bottom))' }}>
      <div className="brief-scroll-hidden" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 9 }}>
        <ModeChips controller={controller} mobile />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, border: `1px solid ${C.line2}`, borderRadius: 22, background: C.white, padding: '7px 7px 7px 14px' }}>
        <textarea
          rows={1}
          value={controller.draft}
          onChange={(e) => controller.setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              controller.send();
            }
          }}
          placeholder={placeholder}
          style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', background: 'transparent', fontFamily: SANS, fontSize: 15.5, lineHeight: 1.45, color: C.ink, maxHeight: 120, minHeight: 24, padding: '6px 0' }}
        />
        <SendButton controller={controller} size={38} font={18} />
      </div>
    </div>
  );
}

function MobileLayout({ controller, onLogout }: { controller: BriefController; onLogout?: () => void }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const onChat = controller.screen === 'chat';
  const sectionTitle = NAV.find((n) => n.screen === controller.screen)?.label ?? 'Бриф';
  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100dvh', width: '100%', maxWidth: 440, margin: '0 auto', overflow: 'hidden', background: C.white, ...FONT_ROOT }}>
      <header style={{ flexShrink: 0, height: 56, display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', borderBottom: `1px solid ${C.line4}`, background: C.white, zIndex: 30 }}>
        <button onClick={() => setDrawerOpen(true)} style={{ width: 44, height: 44, border: 'none', background: 'none', borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 20, color: C.ink2 }}>☰</button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {onChat && <Logo size={24} radius={7} font={13} />}
          <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-.01em' }}>{onChat ? 'Бриф' : sectionTitle}</div>
        </div>
        <button onClick={controller.goHome} style={{ width: 44, height: 44, border: 'none', background: 'none', borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 24, color: C.ink2, fontWeight: 300 }}>+</button>
      </header>

      {controller.screen !== 'chat' ? (
        <OpsRouter controller={controller} mobile />
      ) : controller.view === 'home' ? (
        <MobileHome controller={controller} />
      ) : (
        <MobileChat controller={controller} />
      )}

      {controller.screen === 'chat' && <MobileComposer controller={controller} />}

      {drawerOpen && <MobileDrawer controller={controller} onClose={() => setDrawerOpen(false)} onLogout={onLogout} onOpenSettings={() => setSettingsOpen(true)} />}
      {settingsOpen && (
        <SettingsSheet timezone={controller.timezone} onSave={controller.setTimezone} onClose={() => setSettingsOpen(false)} />
      )}
      {controller.toast && <Toast text={controller.toast} bottom={128} />}
    </div>
  );
}

// ── Entry ────────────────────────────────────────────────────────

export default function BriefPage({ onLogout }: { onLogout?: () => void }) {
  const controller = useBrief();
  const isMobile = useIsMobile();
  return isMobile ? <MobileLayout controller={controller} onLogout={onLogout} /> : <DesktopLayout controller={controller} onLogout={onLogout} />;
}
